import type { AccessibleTenant } from '@cms/contracts'
import { useSession, type Person } from '../auth/session.js'
import type { ProjectAccessCapability } from '../domain/capabilities.js'
import { canManageInTenant, peopleManagementOpen } from '../domain/tenants.js'
import { useSiteWorkspace } from './site.js'

export interface TenantAdmin {
  person: Person | null
  tenant: AccessibleTenant | null
  /** The tenant every call of these screens names in its path: the one picked in the top bar. */
  tenantId: string
  /** Admin of this tenant, as far as the interface can tell. */
  authority: boolean
  capability: ProjectAccessCapability
  /** People screens: authority and a gateway that can manage members. */
  canManagePeople: boolean
  /** API keys: authority is enough, keys do not depend on project enforcement. */
  canManageKeys: boolean
  /** Why the people screens are closed, as an i18n key; null when they are open. */
  peopleLockedKey: string | null
}

/** One answer, for the menu and for the screens, to "may this person manage this client?". */
export function useTenantAdmin(): TenantAdmin {
  const { state, projectAccessCapability } = useSession()
  const { tenant } = useSiteWorkspace()
  const person = state.status === 'signed-in' ? state.person : null
  const authority = canManageInTenant(person, tenant)
  const open = peopleManagementOpen(projectAccessCapability)
  return {
    person,
    tenant,
    tenantId: tenant?.id ?? person?.tenantId ?? '',
    authority,
    capability: projectAccessCapability,
    canManagePeople: authority && open,
    canManageKeys: authority,
    peopleLockedKey: !authority ? 'people.forbidden'
      : open ? null
      : projectAccessCapability === 'loading' ? 'people.capabilityLoading' : 'people.capabilityUnavailable',
  }
}
